import {
  BorderStyle,
  Box,
  Color,
  Display,
  FlexDirection,
  Space,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { type DashboardExperienceOrderResponse } from '@library/api-dashboard-experience'
import React from 'react'
import { browserHistory } from 'react-router'
import { OrderTileActions } from './OrderTileSubcomponents/OrderTileActions'
import { OrderTileHeader } from './OrderTileSubcomponents/OrderTileHeader'
import { OrderTileRecipes } from './OrderTileSubcomponents/OrderTileRecipes'
import { ResponsiveManageDeliveryPanel } from './OrderTileSubcomponents/ResponsiveManageDeliveryPanel'
import { useOrderDeliveryDetails } from '../../hooks/useOrderDeliveryDetails'
import { useOrderTracking } from '../../hooks/useOrderTracking'

type OrderTileProps = {
  order: DashboardExperienceOrderResponse
}

const OrderTileContainer = withCitrusTheme(Box, (theme) => [
  theme.responsiveColor(
    [Color.White, Color.White, Color.White, Color.ColdGrey_100],
    ['borderTopColor'],
  ),
  theme.responsiveStyle(
    ['0px', '0px', '0px', '8px'],
    ['borderTopLeftRadius', 'borderTopRightRadius'],
  ),
  {
    borderBottomLeftRadius: '8px',
    borderBottomRightRadius: '8px',
  },
])

export function OrderTile({ order }: OrderTileProps) {
  const { trackGetHelpWithThisBoxClick, trackOrderExpandClick } = useOrderTracking()
  const { orderDeliveryDetails } = useOrderDeliveryDetails(order.id)
  const [isManageDeliveryExpanded, setIsManageDeliveryExpanded] = React.useState(false)
  const originalPrice = typeof order.price.original === 'string' ? order.price.original : null
  const currentPrice = typeof order.price.amount === 'string' ? order.price.amount : null
  const handleGetHelpClick = () => {
    trackGetHelpWithThisBoxClick({
      orderId: order.id,
    })
    browserHistory.push(`/get-help?orderId=${order.id}`)
  }
  const handleManageDeliveryClick = () => {
    trackOrderExpandClick({
      order,
    })
    setIsManageDeliveryExpanded((currentValue) => !currentValue)
  }
  return (
    <OrderTileContainer
      data-testid="order-tile"
      width={'100%'}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      borderWidth={0.5}
      borderColor={Color.ColdGrey_100}
      borderStyle={BorderStyle.Solid}
      bg={Color.White}
      style={{ overflow: 'hidden' }}
    >
      <Box paddingV={4} paddingH={4}>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
          <OrderTileHeader
            currentPrice={currentPrice}
            isManageDeliveryExpanded={isManageDeliveryExpanded}
            onManageDeliveryClick={handleManageDeliveryClick}
            order={order}
            originalPrice={originalPrice}
          />
          <Space size={[1, 1, 1, 4]} direction="vertical" />
          <OrderTileRecipes orderId={order.id} recipes={order.recipes} />
          <Space size={4} direction="vertical" />
          <OrderTileActions
            helpCta={order.help_cta}
            isManageDeliveryExpanded={isManageDeliveryExpanded}
            onGetHelpClick={handleGetHelpClick}
            onManageDeliveryClick={handleManageDeliveryClick}
          />
        </Box>
      </Box>
      <ResponsiveManageDeliveryPanel
        isManageDeliveryExpanded={isManageDeliveryExpanded}
        orderDeliveryDetails={orderDeliveryDetails}
      />
    </OrderTileContainer>
  )
}
